/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { FC, useContext, useEffect, useState } from 'react';
import styles from './clients.module.scss';
import { ObraProperties, WandaProject } from '../../../types/ObraDetails';
import { ValidationError } from '../../../types/ValidationError';
import clientPicture from '../../../assets/client.svg';
import { useTranslation } from 'react-i18next';
import InformationIcon from '../components/informationIcon/informationIcon';
import ClientBox from './components/clientBox/clientBox';
import EditClients from './editClients/editClients';
import { UserModeContext } from '../../../context/UserModeProvider';
import ViewClients from './viewClients/viewClients';
import ObrasAPI from '../../../services/APIObras';
import ErrorDetails from '../components/ErrorDetails/ErrorDetails';
import { DetailsViewContext } from '../../../context/DetailsViewProvider';
import { SaveContext } from '../../../context/SaveProvider';
import { ClientsLoadingSkeleton } from './components/clientsLoadingSkeleton/clientsLoadingSkeleton';


interface ClientsProps {
  obra: ObraProperties;
  codCentro: string;
  wandaProject?: WandaProject;
  wandaProjectLoading: boolean;
  wandaProjectError: boolean;
  version: 'viewer' | 'editor';
  errors?: ValidationError[];
}

const Clients: FC<ClientsProps> = (props) => {
  const {
    obra,
    codCentro,
    wandaProject,
    wandaProjectLoading,
    wandaProjectError,
    version,
    errors,
  } = props;
  const [t, i18n] = useTranslation();
  const userModeContext = useContext(UserModeContext);
  const {
    setObra,
    loadedDetailSections,
    markDetailSectionLoaded,
  } = useContext(DetailsViewContext);
  const saveContext = useContext(SaveContext);
  const isEditorMode = userModeContext.userMode === 'editor';
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const sectionKey = `${codCentro}:${version}:${i18n.language}:clients`;

  useEffect(() => {
    let active = true;
    setClientsLoaded(false);
    setLoadError(false);

    if (!wandaProject) return () => {
      active = false;
    };

    if (loadedDetailSections[sectionKey]) {
      setClientsLoaded(true);
      return () => {
        active = false;
      };
    }

    ObrasAPI.getInstance().getObraClientsAPI(codCentro, wandaProject, version)
      .then((response) => {
        if (!active) return;
        setObra({
          ...obra,
          cliente: response.cliente,
        });
        markDetailSectionLoaded(sectionKey);
        setClientsLoaded(true);
      })
      .catch(() => {
        if (active) setLoadError(true);
      });

    return () => {
      active = false;
    };
  }, [codCentro, wandaProject, version, i18n.language, saveContext.reloadData, loadedDetailSections, markDetailSectionLoaded, sectionKey]);

  if (loadError || wandaProjectError) return <ErrorDetails />;
  if (!clientsLoaded || wandaProjectLoading) return <ClientsLoadingSkeleton isEditorMode={isEditorMode} />;

  // data for section client
  const clientInfo = [
    { title: t('details.clients.clientName'), value: (obra.cliente.nomCliente?.length > 0) ? obra.cliente.nomCliente : t('details.notInformed') },
    { title: t('details.clients.clientCode'), value: obra.cliente.codCliente ? obra.cliente.codCliente : t('details.notInformed') },
  ];

  return (
    <div
      data-testid="clients-test"
      className={styles.clientContainer}>

      <div id='client' className={styles.heading}>{t('details.clients.client')}
        <InformationIcon message={t('help.clients.client')} />
      </div>
      <div className={styles.box}>
        <div className={styles.boxContainerChild}>
          <ClientBox identifier='client' picture={clientPicture} items={clientInfo} />
        </div>
      </div>
      {isEditorMode ? <EditClients obra={obra} errors={errors} /> : <ViewClients obra={obra} />}
      <div className='footer' style={{marginBottom: '75px'}}></div>
    </div>);
};

export default Clients;
